import axios, { AxiosInstance } from 'axios';
import { deserialise } from 'kitsu-core';

// With hoistData: true, kitsu-core:
// - Single resources: hoisted directly → { id, type, ...attributes, ...relationships }
// - Collections (arrays): keeps wrapper → { data: Array<T>, meta?, links? }
// Callers should type accordingly: T for single resources, ICollectionResponse<T> for collections
export interface ICollectionResponse<T = any> {
  data: Array<T>;
  meta?: Record<string, any>;
  links?: Record<string, string>;
}

// Recursively replaces undefined with null for Next.js serialization
// Handles circular references by tracking seen objects
export function sanitizeForSerialization<T>(obj: T, seen = new WeakSet()): T {
  if (obj === undefined) return null as T;
  if (obj === null || typeof obj !== 'object') return obj;

  // True circular reference (ancestor still on the current path) — break the cycle
  if (seen.has(obj as object)) {
    return null as T;
  }

  seen.add(obj as object);

  let result: T;

  if (Array.isArray(obj)) {
    result = obj.map((item) => sanitizeForSerialization(item, seen)) as T;
  } else {
    const res: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      res[key] = sanitizeForSerialization(value, seen);
    }

    result = res as T;
  }

  // Remove after recursing so sibling branches can reference the same object
  // (kitsu shares included resources across multiple primary records — not a cycle)
  seen.delete(obj as object);

  return result;
}

export class JsonApiClient {
  private readonly client: AxiosInstance;

  constructor(baseURL: string) {
    this.client = axios.create({
      baseURL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
      },
    });
  }

  async get<T = any>(endpoint: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
    const response = await this.client.get(endpoint, { params, headers });

    return this.processResponse<T>(response.data);
  }

  async post<T = any>(endpoint: string, data: unknown, params?: Record<string, string>): Promise<T> {
    const response = await this.client.post(endpoint, data, { params });

    return this.processResponse<T>(response.data);
  }

  async put<T = any>(endpoint: string, data: unknown): Promise<T> {
    const response = await this.client.put(endpoint, data);

    return this.processResponse<T>(response.data);
  }

  async patch<T = any>(endpoint: string, data: unknown): Promise<T> {
    const response = await this.client.patch(endpoint, data);

    return this.processResponse<T>(response.data);
  }

  // Deserialize JSON:API response and sanitize for Next.js serialization
  private processResponse<T>(data: unknown): T {
    // keep in mind kitsu pulls out resource out of data property, and puts it into root in a case of a single resource response
    // in a case of a array, it keeps the root data!
    const deserialized = deserialise(data, { hoistData: true });

    // sanitize because this version of next.js don't like undefined for a properties (delete later when upgrading next.js)
    return sanitizeForSerialization(deserialized) as T;
  }

  async delete(endpoint: string): Promise<void> {
    await this.client.delete(endpoint);
  }
}

export const apiClient = new JsonApiClient(process.env.NEXT_PUBLIC_API_URL || '');
